import { Capacitor } from '@capacitor/core';

declare global {
  interface Window {
    /** Loaded from env.js before the JavaScript bundle (native only). */
    __CC_API_URL__?: string;
    __CC_LD_CLIENT_ID__?: string;
  }
}

/**
 * Resolve the backend API base URL.
 *
 * Browser / Vite / nginx always use relative `/api` so native bake-time env
 * (VITE_API_URL in `.env.local`) cannot bleed into the classic web app.
 *
 * Native packaged apps:
 * 1. `env.js` (`window.__CC_API_URL__`) — Dev→dev, Prod→prod
 * 2. `VITE_API_URL` bake-time fallback
 * 3. Relative `/api` last resort
 */
export function resolveApiBaseUrl(): string {
  if (typeof window === 'undefined' || !Capacitor.isNativePlatform()) {
    return '/api';
  }

  const injected = window.__CC_API_URL__;
  if (typeof injected === 'string' && injected.trim().length > 0) {
    return injected.trim().replace(/\/$/, '');
  }

  const fromEnv = import.meta.env.VITE_API_URL as string | undefined;
  if (typeof fromEnv === 'string' && fromEnv.trim().length > 0) {
    return fromEnv.trim().replace(/\/$/, '');
  }

  return '/api';
}

/** LaunchDarkly client-side ID: native override, then Vite env (web + native). */
export function resolveLdClientId(): string | undefined {
  if (typeof window !== 'undefined') {
    const injected = window.__CC_LD_CLIENT_ID__;
    if (typeof injected === 'string' && injected.trim().length > 0) {
      return injected.trim();
    }
  }
  const fromEnv = import.meta.env.VITE_LD_CLIENT_ID as string | undefined;
  return fromEnv?.trim() || undefined;
}
